import { h, render } from "preact";
import { App, type AppOptions } from "./App";
import { styles } from "./styles";
import type { InitOptions, ThemeColors, WidgetHandle } from "./types";

// Option key -> CSS custom property. CSS variables inherit through the
// shadow boundary, so setting them on the host overrides the :host
// defaults (and the per-selector fallbacks for the per-element tokens).
const THEME_VARS: Record<keyof ThemeColors, string> = {
  accentColor: "--ai-accent",
  accentForeground: "--ai-accent-fg",
  surfaceColor: "--ai-bg-surface",
  userBubbleColor: "--ai-user-bubble-bg",
  cardActionTextColor: "--ai-card-action-fg",
  sendIconColor: "--ai-send-icon",
};

// brand name, welcome message, and starter prompts default values + the
// API-config-fetch path live in App.tsx now. mount.ts just passes init
// options through; App.tsx decides whether to fetch /v1/config or use
// the explicit values.

export function createWidget(options: InitOptions): WidgetHandle {
  if (!options.target) {
    throw new Error('AIWidget: "target" is required');
  }
  const host =
    typeof options.target === "string"
      ? document.querySelector<HTMLElement>(options.target)
      : options.target;
  if (!host) {
    throw new Error(
      `AIWidget: target not found: ${String(options.target)}`,
    );
  }

  // Shadow DOM mount: scoped styles and DOM, immune to host page CSS.
  const shadow = host.attachShadow({ mode: "open" });
  const styleEl = document.createElement("style");
  styleEl.textContent = styles;
  const container = document.createElement("div");
  // The container fills the host via absolute positioning, anchored to the
  // host's `position: relative` from :host. This bypasses the height:100%
  // percentage chain entirely — that chain is brittle when any ancestor
  // breaks `height` propagation, which hosts inside heavy CMS layouts often
  // do.
  container.style.cssText = "position: absolute; inset: 0; display: block;";
  shadow.appendChild(styleEl);
  shadow.appendChild(container);

  // Theme: write per-client CSS custom properties onto the host element.
  // Applied twice: right now with the init/data-attribute values for an
  // instant first paint, and again by App.tsx with the /v1/config values
  // once fetched (the admin-saved DB values are the source of truth and
  // win over the snippet attributes).
  const applyTheme = (colors: ThemeColors) => {
    for (const [key, cssVar] of Object.entries(THEME_VARS)) {
      const value = colors[key as keyof ThemeColors];
      if (value) host.style.setProperty(cssVar, value);
    }
  };
  applyTheme(options);

  const appOptions: AppOptions = {
    clientId: options.clientId,
    apiUrl: options.apiUrl.replace(/\/$/, ""),
    // Undefined values flow through — App.tsx fetches /v1/config to fill
    // them in. Explicit values (from the admin test page's imperative
    // init) override the fetch and skip the network round-trip entirely.
    brandName: options.brandName,
    welcomeMessage: options.welcomeMessage,
    starterPrompts: options.starterPrompts,
    applyTheme,
  };

  render(h(App, { options: appOptions }), container);

  // Keep the widget sized to the space actually available below its own top
  // edge, so the message input bar is always visible without scrolling the
  // host page. The install snippet is a bare <div> with no height, and a
  // static CSS cap (vh/px) can't know how far down the page the widget is
  // embedded — header, hero, and intro copy above it push it down. On a tall
  // page a fixed-height panel then overruns the viewport and hides the input.
  // We measure the host's viewport position and set its height to the visible
  // remainder, clamped to a usable range, recomputing on anything that can
  // move it. The panel's internal `.messages` area scrolls within whatever
  // height we land on, so content is never lost — only the chrome stays put.
  const FIT_BOTTOM_MARGIN_PX = 16;
  const FIT_MIN_PX = 200;
  const FIT_MAX_PX = 720;
  let fitRaf = 0;
  const applyFit = () => {
    fitRaf = 0;
    // Negative top means the widget's top has scrolled above the viewport;
    // clamp to 0 so it gets the full window height (up to FIT_MAX_PX).
    const top = Math.max(host.getBoundingClientRect().top, 0);
    const visible = window.innerHeight - top - FIT_BOTTOM_MARGIN_PX;
    const next = Math.max(FIT_MIN_PX, Math.min(FIT_MAX_PX, visible));
    host.style.setProperty("height", `${next}px`, "important");
    // Neutralize the :host min-height floor (and any embed min-height) so the
    // widget can shrink to fit when there's little room below it.
    host.style.setProperty("min-height", "0", "important");
  };
  const scheduleFit = () => {
    if (!fitRaf) fitRaf = requestAnimationFrame(applyFit);
  };
  applyFit();
  window.addEventListener("resize", scheduleFit, { passive: true });
  window.addEventListener("scroll", scheduleFit, { passive: true });
  // Catch layout shifts that don't fire scroll/resize (late-loading content
  // above the widget, fonts, images reflowing the page).
  const ro =
    typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(scheduleFit)
      : null;
  ro?.observe(document.documentElement);

  return {
    destroy: () => {
      window.removeEventListener("resize", scheduleFit);
      window.removeEventListener("scroll", scheduleFit);
      ro?.disconnect();
      if (fitRaf) cancelAnimationFrame(fitRaf);
      render(null, container);
    },
  };
}
